// Centralized logger utility gated by VITE_LOG_LEVEL
// Levels: debug < info < warn < error < silent

import { env, envVars } from "@/shared/config/env";

type LogLevel = "debug" | "info" | "warn" | "error" | "silent";

const ORDER: Record<Exclude<LogLevel, "silent">, number> = {
	debug: 10,
	info: 20,
	warn: 30,
	error: 40,
};

const LOG_LEVELS: readonly LogLevel[] = [
	"debug",
	"info",
	"warn",
	"error",
	"silent",
];

function isLogLevel(value: string): value is LogLevel {
	return (LOG_LEVELS as readonly string[]).includes(value);
}

function parseLogLevel(raw: string | undefined): LogLevel | undefined {
	if (!raw) {
		return undefined;
	}
	const normalized = raw.toLowerCase();
	return isLogLevel(normalized) ? normalized : undefined;
}

const MIN_LEVEL: LogLevel =
	parseLogLevel(envVars.logLevel) ??
	(env.mode === "production" ? "warn" : "debug");

function shouldLog(level: Exclude<LogLevel, "silent">): boolean {
	if (MIN_LEVEL === "silent") {
		return false;
	}
	// MIN_LEVEL is narrowed to the non-silent branch above, so we can read
	// from ORDER without another assertion.
	const min = ORDER[MIN_LEVEL];
	return ORDER[level] >= min;
}

/**
 * Log metadata that can be attached to log entries
 */
export type LogMetadata = Record<string, unknown> & {
	[key: string]: unknown;
};

/**
 * Log entry data structure
 */

/**
 * Logger interface with typed methods
 */
export interface Logger {
	debug: (message: string, metadata?: LogMetadata) => void;
	error: (message: string, metadata?: LogMetadata | Error) => void;
	info: (message: string, metadata?: LogMetadata) => void;
	warn: (message: string, metadata?: LogMetadata) => void;
}

type JsonSafe =
	| null
	| boolean
	| number
	| string
	| JsonSafe[]
	| { [key: string]: JsonSafe };

function serializeError(error: Error): JsonSafe {
	const cause = error.cause;

	return {
		kind: "Error",
		name: error.name,
		message: error.message,
		stack: error.stack ?? null,
		cause: cause === undefined ? null : toJsonSafe(cause),
	};
}

function isResponse(value: unknown): value is Response {
	return typeof Response !== "undefined" && value instanceof Response;
}

/** Convert a non-object primitive to a JSON-safe value. Returns `undefined` when `value` is not a primitive. */
function primitiveToJsonSafe(value: unknown): JsonSafe | undefined {
	if (value === null) {
		return null;
	}
	if (typeof value === "string") {
		return value;
	}
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : String(value);
	}
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value === "bigint") {
		return `${value}n`;
	}
	if (typeof value === "undefined") {
		return "undefined";
	}
	if (typeof value === "function") {
		// Function.name is typed as string; guard defensively against
		// subclasses that override it with a non-string value.
		const maybeName: unknown = value.name;
		const name =
			typeof maybeName === "string" && maybeName ? maybeName : "anonymous";
		return `[Function ${name}]`;
	}
	if (typeof value === "symbol") {
		return value.toString();
	}

	return undefined;
}

/** Serialize a Response instance to a JSON-safe object. */
function serializeResponse(value: Response): JsonSafe {
	const urlCandidate = Reflect.get(value, "url");
	const statusCandidate = Reflect.get(value, "status");
	const statusTextCandidate = Reflect.get(value, "statusText");

	return {
		kind: "Response",
		url: typeof urlCandidate === "string" ? urlCandidate : null,
		status: typeof statusCandidate === "number" ? statusCandidate : null,
		statusText:
			typeof statusTextCandidate === "string" ? statusTextCandidate : null,
	};
}

/** Copy common non-enumerable error-like fields (message, name, stack) into the record. */
function enrichErrorLikeFields(
	obj: object,
	record: Record<string, JsonSafe>,
): void {
	const bag = obj as Record<string, unknown>;
	if (!("message" in record)) {
		const messageCandidate = bag.message;
		if (typeof messageCandidate === "string") {
			record.message = messageCandidate;
		}
	}
	if (!("name" in record)) {
		const nameCandidate = bag.name;
		if (typeof nameCandidate === "string") {
			record.name = nameCandidate;
		}
	}
	if (!("stack" in record)) {
		const stackCandidate = bag.stack;
		if (typeof stackCandidate === "string") {
			record.stack = stackCandidate;
		}
	}
}

/** Produce a fallback representation for objects whose enumerable entries are empty. */
function serializeEmptyObject(value: unknown): JsonSafe {
	// Probe dynamic properties through Record<string, unknown> so no nested
	// shape assertion is required; each lookup stays typed as `unknown`.
	let constructorNameCandidate: unknown;
	if (value !== null && typeof value === "object") {
		const bag = value as Record<string, unknown>;
		const ctor = bag.constructor;
		if (ctor !== null && typeof ctor === "object") {
			constructorNameCandidate = (ctor as Record<string, unknown>).name;
		} else if (typeof ctor === "function") {
			constructorNameCandidate = ctor.name;
		}
	}

	const constructorName =
		typeof constructorNameCandidate === "string" &&
		constructorNameCandidate.trim().length > 0
			? constructorNameCandidate
			: "Object";

	let stringRepr: string | null = null;
	try {
		stringRepr = String(value);
	} catch {
		stringRepr = null;
	}

	return {
		kind: "UnknownObject",
		constructorName,
		string: stringRepr,
	};
}

/** Serialize a plain object (non-null, non-array, non-Error, non-Date, non-Response) to JSON-safe. */
function objectToJsonSafe(
	value: object,
	depth: number,
	maxDepth: number,
	seen: WeakSet<object>,
): JsonSafe {
	if (seen.has(value)) {
		return "[Circular]";
	}
	seen.add(value);

	if (depth >= maxDepth) {
		return "[Object]";
	}

	const record: Record<string, JsonSafe> = {};

	for (const [key, entryValue] of Object.entries(
		value as Record<string, unknown>,
	)) {
		record[key] = toJsonSafe(entryValue, {
			depth: depth + 1,
			maxDepth,
			seen,
		});
	}

	enrichErrorLikeFields(value, record);

	if (Object.keys(record).length === 0) {
		return serializeEmptyObject(value);
	}

	return record;
}

function toJsonSafe(
	value: unknown,
	options?: { depth?: number; maxDepth?: number; seen?: WeakSet<object> },
): JsonSafe {
	const depth = options?.depth ?? 0;
	const maxDepth = options?.maxDepth ?? 6;
	const seen = options?.seen ?? new WeakSet<object>();

	const primitive = primitiveToJsonSafe(value);
	if (primitive !== undefined) {
		return primitive;
	}

	if (value instanceof Error) {
		return serializeError(value);
	}

	if (value instanceof Date) {
		return { kind: "Date", iso: value.toISOString() };
	}

	if (isResponse(value)) {
		return serializeResponse(value);
	}

	if (Array.isArray(value)) {
		if (depth >= maxDepth) {
			return `[Array(${value.length})]`;
		}
		return value.map((item) =>
			toJsonSafe(item, { depth: depth + 1, maxDepth, seen }),
		);
	}

	if (value !== null && typeof value === "object") {
		return objectToJsonSafe(value, depth, maxDepth, seen);
	}

	return String(value);
}

function formatLogArgs(
	message: string,
	metadata?:
		| LogMetadata
		| Error
		| { message: string; name?: string; stack?: string; cause?: unknown },
): unknown[] {
	if (!metadata) {
		return [message];
	}

	if (metadata instanceof Error) {
		return [message, serializeError(metadata)];
	}

	return [message, toJsonSafe(metadata)];
}

export const logger: Logger = {
	debug: (message: string, metadata?: LogMetadata) => {
		if (shouldLog("debug") && typeof console !== "undefined") {
			console.debug(...formatLogArgs(message, metadata));
		}
	},
	info: (message: string, metadata?: LogMetadata) => {
		if (shouldLog("info") && typeof console !== "undefined") {
			console.info(...formatLogArgs(message, metadata));
		}
	},
	warn: (message: string, metadata?: LogMetadata) => {
		if (shouldLog("warn") && typeof console !== "undefined") {
			console.warn(...formatLogArgs(message, metadata));
		}
	},
	error: (
		message: string,
		metadata?:
			| LogMetadata
			| Error
			| { message: string; name?: string; stack?: string; cause?: unknown },
	) => {
		if (shouldLog("error") && typeof console !== "undefined") {
			console.error(...formatLogArgs(message, metadata));
		}
	},
};

export type { LogLevel };
